import ApplicationError from '@/errors/application-error';
import CookieHelper from '@/helpers/OldCookieHelper';
import dayjs from 'dayjs';
import ErrorHandlerHelper from '@/helpers/ErrorHandlerHelper';
import { ECartTypeId, ELanguage } from '@ringana/configs';
import { EPhoneType } from '@/common/enums/customers.enum';
import { IAddress } from '@/common/interfaces/shared.interface';
import { ETimeUnit } from '@/common/enums/index.enum';
import { format, parse } from 'date-fns';
import config from '../../config';

export default class FormatHelper {
  public static formatTimeValue(value: number, type: ETimeUnit): string {
    let conversionFactor: number;
    let fraction: number;

    switch(type) {
      case ETimeUnit.MINUTE:
        conversionFactor = 1000 * 60 * 60;
        fraction = 1000 * 60;
        break;
      case ETimeUnit.SECOND:
        conversionFactor = 1000 * 60;
        fraction = 1000;
        break;
      default:
        throw new ApplicationError(`FormatHelper.ts: formatTimeValue(): Invalid type specified!`);
    }

    const formattedValue = Math.floor((value % conversionFactor) / fraction);

    if(formattedValue < 10) {
      return `0${formattedValue}`;
    }
    return formattedValue.toString();
  }

  public static formatDecimalString(num: number, digits = 2): string {
    const language = CookieHelper.getLanguage() || ELanguage.DE;

    return num.toLocaleString(`${language}-${language.toUpperCase()}`, { minimumFractionDigits: digits });
  }

  public static formatAddressCity(address: IAddress): string {
    const temp: string[] = [];
    if(address.zipCode) {
      temp.push(address.zipCode);
    }
    if(address.city) {
      temp.push(address.city);
    }

    return temp.join(` `);
  }

  public static formatAddressStreet(address: IAddress): string {
    const temp: string[] = [];
    if(address.street) {
      temp.push(address.street);
    }
    if(address.streetNumber) {
      temp.push(address.streetNumber);
    }

    return temp.join(` `);
  }

  public static formatPhoneType(type: number): EPhoneType {
    switch(type) {
      case 1:
        return EPhoneType.LANDLINE;
      case 2:
        return EPhoneType.MOBILE;
      case 3:
        return EPhoneType.FAX;
      default:
        ErrorHandlerHelper.handleError(`Phone ${type} unknown!`, { logToConsole: true });
        return EPhoneType.MOBILE;
    }
  }

  public static formatFullAddress(address: IAddress, cartTypeId?: ECartTypeId): string {
    switch(cartTypeId) {
      case ECartTypeId.PERSONAL_USE:
        return window.vueHeader.cartSelectTexts.own;
      case ECartTypeId.MERCHANDISE:
        return window.vueHeader.cartSelectTexts.marchandise;
      default:
        return `${address.recipient}, ${this.formatAddressStreet(address)}, ${this.formatAddressCity(address)} - ${address.country}`;
    }
  }

  public static dateToFormattedDateString(date: Date, dateFormat = `DD.MM.YYYY`): string {
    return dayjs(date).format(dateFormat);
  }

  public static formatPercentages(percentage: number | string): string {
    return `${percentage}%`;
  }

  public static percentageClass(percentage: number | string): string | null {
    if(percentage > 0) {
      return `value--positive`;
    }
    if(percentage < 0) {
      return `value--negative`;
    }
    return null;
  }

  public static getFiltersFetchConfig(filters: {[index: string]: string | undefined}): string {
    let ret: string[] = [];

    for(const filter of Object.keys(filters)) {
      if(Object.prototype.hasOwnProperty.call(filters, filter) && filters[filter]) {
        ret = [...ret, `${filter}=${filters[filter]}`];
      }
    }

    return ret.length > 0 ? `/?${ret.join(`&`)}` : ``;
  }

  public static replaceUnicodeChars(text: string): string {
    let cleanText = text;

    for(const unicode of config.unicodes) {
      if(cleanText.includes(unicode.code)) {
        cleanText = cleanText.replaceAll(unicode.code, unicode.replacement);
      }
    }

    return cleanText;
  }

  public static formatDateForInfoworx(date: string | number): string {
    if(typeof date === `string`) {
      const parsedDate = parse(date, `dd.MM.yyyy`, new Date());
      return format(parsedDate, `yyyy-MM-dd'T'HH:mm:ss`);
    }

    return format(date, `yyyy-MM-dd'T'HH:mm:ss`);
  }
}
